import type { DashboardFilters, TimeRange, WidgetData } from "@nexus/widget-sdk";
import {
  createDateFormatter,
  displayDate,
  formatPeriodDateRange,
  type TimezoneMode,
} from "@nexus/ui";
import { resolveWidgetTitle } from "@nexus/widget-sdk";
import { getWidgetSubjects, getWidgetTitleVariables } from "./WidgetRegistry";

export interface TitleVariableContext {
  timezone?: TimezoneMode;
  timeRange?: TimeRange | null;
  tenantNames?: readonly string[];
  tenantCount?: number;
  allTenants?: boolean;
  eventName?: string;
  campaignName?: string;
  dashboardName?: string;
}

export function titleScopeContext(
  filters: { timeRange?: TimeRange; tenantIds: readonly (string | number)[] },
  tenants: readonly { tenant_id: string; tenant_label: string }[] | undefined,
  dashboardName: string,
  timezone: TimezoneMode = "utc",
): TitleVariableContext {
  const ids = [...new Set(filters.tenantIds.map(String))];
  const selected = ids.length
    ? tenants?.filter((tenant) => ids.includes(tenant.tenant_id))
    : tenants;
  return {
    timezone,
    timeRange: filters.timeRange,
    tenantNames: selected?.map((tenant) => tenant.tenant_label),
    tenantCount: ids.length || selected?.length,
    allTenants: ids.length === 0,
    dashboardName,
  };
}

export function titleEntityContext(
  widgets: readonly { instanceId: string; widgetId: string }[],
  data: Record<string, WidgetData>,
  filters: DashboardFilters,
): Pick<TitleVariableContext, "eventName" | "campaignName"> {
  const eventNames = new Set<string>();
  const campaignNames = new Set<string>();
  const matches = (field: string, id: string) =>
    filters.dataFilters.some(
      (filter) => filter.field === field && filter.operator === "eq" && String(filter.value) === id,
    );
  // What each widget's data says the page is about, kept when the dashboard's filters select it.
  for (const widget of widgets) {
    for (const subject of getWidgetSubjects(widget.widgetId, data[widget.instanceId])) {
      if (subject.kind === "event" && matches("event_id", subject.id))
        eventNames.add(subject.label);
      if (subject.kind === "protected-event" && matches("protected_event_id", subject.id)) {
        eventNames.add(subject.label);
        if (subject.campaign) campaignNames.add(subject.campaign.label);
      }
      if (subject.kind === "campaign" && matches("campaign_id", subject.id))
        campaignNames.add(subject.label);
    }
  }
  return {
    eventName: eventNames.size === 1 ? [...eventNames][0] : undefined,
    campaignName: campaignNames.size === 1 ? [...campaignNames][0] : undefined,
  };
}

const month = createDateFormatter("utc", { month: "short" }, "en-US");

function dates(context: TitleVariableContext): [Date, Date] | undefined {
  const start = Date.parse(context.timeRange?.from ?? "");
  const end = Date.parse(context.timeRange?.to ?? "");
  return Number.isFinite(start) && Number.isFinite(end) && end > start
    ? [
        displayDate(new Date(start), context.timezone ?? "utc"),
        displayDate(new Date(end - 1), context.timezone ?? "utc"),
      ]
    : undefined;
}

function shortDate(date: Date): string {
  return `${date.getUTCDate()} ${month.format(date)}`;
}

function range(context: TitleVariableContext, compact: boolean): string | undefined {
  return formatPeriodDateRange(
    context.timeRange?.from ?? "",
    context.timeRange?.to ?? "",
    context.timezone ?? "utc",
    compact,
  );
}

export const TITLE_VARIABLES: readonly {
  name: string;
  label: string;
  example: string;
  resolve: (context: TitleVariableContext) => string | undefined;
}[] = [
  {
    name: "period.range",
    label: "Period dates",
    example: "21\u201327 September 2026",
    resolve: (context) => range(context, false),
  },
  {
    name: "period.range_compact",
    label: "Compact period dates",
    example: "21\u201327 Sep",
    resolve: (context) => range(context, true),
  },
  {
    name: "period.label",
    label: "Period label",
    example: "Last 7 days",
    resolve: (context) => (dates(context) ? context.timeRange?.label : undefined),
  },
  {
    name: "period.start_date",
    label: "Start date",
    example: "21 Sep",
    resolve: (context) => {
      const bounds = dates(context);
      return bounds ? shortDate(bounds[0]) : undefined;
    },
  },
  {
    name: "period.end_date",
    label: "Last included date",
    example: "27 Sep",
    resolve: (context) => {
      const bounds = dates(context);
      return bounds ? shortDate(bounds[1]) : undefined;
    },
  },
  {
    name: "tenant.label",
    label: "Tenant scope",
    example: "Canal+ Africa",
    resolve: (context) =>
      context.allTenants
        ? "All tenants"
        : context.tenantCount === 1
          ? context.tenantNames?.[0]
          : context.tenantCount
            ? `${context.tenantCount} tenants`
            : undefined,
  },
  {
    name: "tenant.name",
    label: "Tenant name",
    example: "Canal+ Africa",
    resolve: (context) => (context.tenantCount === 1 ? context.tenantNames?.[0] : undefined),
  },
  {
    name: "tenant.count",
    label: "Tenant count",
    example: "3",
    resolve: (context) =>
      context.tenantCount === undefined ? undefined : String(context.tenantCount),
  },
  {
    name: "event.name",
    label: "Event name",
    example: "PSG vs Tottenham",
    resolve: (context) => context.eventName,
  },
  {
    name: "campaign.name",
    label: "Campaign name",
    example: "Champions League",
    resolve: (context) => context.campaignName,
  },
  {
    name: "dashboard.name",
    label: "Dashboard name",
    example: "Piracy audience Africa",
    resolve: (context) => context.dashboardName,
  },
];

/**
 * Replace the dashboard's variables in a text. Variables a widget fills itself
 * (`widgetVariables`, see `WidgetDefinition.titleVariables`) are left for it,
 * without an issue.
 */
export function resolveTextTemplate(
  template: string,
  context: TitleVariableContext,
  allowedVariables?: readonly string[],
  widgetVariables?: readonly string[],
) {
  const issues = new Map<string, "unknown" | "unavailable" | "incompatible">();
  const text = template.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (token, rawName: string) => {
    const name = rawName.trim();
    if (widgetVariables?.includes(name)) return token;
    const variable = TITLE_VARIABLES.find((variable) => variable.name === name);
    if (variable && allowedVariables && !allowedVariables.includes(name)) {
      issues.set(name, "incompatible");
      return token;
    }
    const value = variable?.resolve(context);
    if (value === undefined || value === "") {
      issues.set(name, variable ? "unavailable" : "unknown");
      return token;
    }
    return value;
  });
  return { text, issues: [...issues].map(([name, reason]) => ({ name, reason })) };
}

export function resolveTitleTemplate(template: string, context: TitleVariableContext) {
  const result = resolveTextTemplate(template, context);
  return { title: result.text, issues: result.issues };
}

export function resolveTitleConfig<T extends Record<string, unknown>>(
  config: T,
  context: TitleVariableContext,
): T {
  return typeof config.title === "string"
    ? { ...config, title: resolveTitleTemplate(config.title, context).title }
    : config;
}

export function insertTitleVariable(
  template: string,
  name: string,
  start = template.length,
  end = start,
): string {
  return `${template.slice(0, start)}{{${name}}}${template.slice(end)}`;
}

/**
 * A widget title as plain text, where only text fits (a list row, a menu, a
 * drag image): the variables the widget fills from its own data are left out,
 * so "{{country.flag}} {{country.name}}" is empty and the caller names the widget.
 */
export function widgetTitleText(widgetId: string, title: unknown): string {
  if (typeof title !== "string") return "";
  const variables = getWidgetTitleVariables(widgetId);
  if (variables.length === 0) return title.trim();
  const text = resolveWidgetTitle(
    title,
    Object.fromEntries(variables.map((variable) => [variable.name, undefined])),
  );
  return typeof text === "string" ? text.trim() : "";
}
